import { 
    PropsWithChildren, 
    useCallback, 
    useMemo,
    createContext,
    useReducer,
    useContext,
    useEffect
 } from "react";
import { useGetTickets } from "@hooks/use-get/tickets";
import { useUpdateTicket } from "@hooks/use-update/ticket";
import { useDeleteTicket } from "@hooks/use-delete/ticket";
import { useScreenAlert } from "@hooks/use-screen-alert";
import { useUserContext } from "@hooks/use-auth-context";
import { useMultiSiteContext } from "@hooks/use-site-context";
import { getLocalStorageItem } from "@utils/index";
import { 
    TableContextActions 
} from "@hooks/use-table-context/types";
import { 
    INITIAL_CONTEXT_DISPATCH, 
    INITIAL_CONTEXT_STATE,
    ID_KEY, 
    HIDDEN_COLUMNS_KEY,
    buildFetchParams
} from "@hooks/use-table-context/utils";
import { 
    useCommonTableActions, 
    useTableDataFetcher 
} from "@hooks/use-table-context";
import { 
    TicketTableContextValue, 
    TicketTableDispatchValue, 
    TicketTableData,
    TicketTableFilter
 } from "./types";
import { 
    TicketTableStateHandler, 
    TICKET_FILTER_TABLE_KEY,
    TICKET_FILTER_SCHEMA_VERSION,
} from "./utils";
import {
    loadPersistedTableFilterState,
    savePersistedTableFilterState,
} from "@hooks/use-table-context/persistence/table-filter-storage";


// Instantiates the table context and sets the object schema each Context is anticipating

export const TicketTableContext = createContext<TicketTableContextValue>({...INITIAL_CONTEXT_STATE, showClosed: false});

export const TicketTableDispatchContext = createContext<TicketTableDispatchValue>(
    {
        ...INITIAL_CONTEXT_DISPATCH,  
        toggleShowClosedTickets: () => {},
    });

/* Creates the function to update the context state based on action type and payload, 
    using the TicketTableStateHandler to manage state updates, called in the actual React reducer later */
const ticketTableContextReducer = (
    state: TicketTableContextValue,
    action: { type: TableContextActions; payload: any; }
    ) => {
    const stateHandler = new TicketTableStateHandler(state);
    stateHandler[action.type](action.payload as never);
    const updatedState = stateHandler.getState();
    return updatedState; // Placeholder return
}

export interface TicketTableContextProviderProps extends PropsWithChildren {
    /**
     * Distinguishes multiple simultaneous mounts of the Ticket table (e.g. the
     * standalone Tickets page vs. one or more dashboard widgets) so persisted
     * filters don't overwrite each other. Omit only when a single instance can
     * ever exist at a time.
     */
    instanceId?: string;
}

export const TicketTableContextProvider = ({ children, instanceId } : TicketTableContextProviderProps) => {
        const { siteId } = useMultiSiteContext();

        // Read once at mount — same timing as the legacy hiddenColumns-only read
        // below, and before UserContext has hydrated (see table-filter-storage.ts).
        const persistedFilterState = useMemo(
            () =>
                loadPersistedTableFilterState<TicketTableFilter>(
                    TICKET_FILTER_TABLE_KEY,
                    siteId,
                    TICKET_FILTER_SCHEMA_VERSION,
                    instanceId,
                ),
            [], // eslint-disable-line react-hooks/exhaustive-deps
        );

        // Hidden column logic from use-asset-table-context - memoized to avoid recalculation on every render.
        // Falls back to the old global hiddenColumns key so existing users' column
        // preferences migrate forward the first time they load with no scoped filter state saved yet.
        const initialHiddenColumns = useMemo(() => {
            if (persistedFilterState) return persistedFilterState.hiddenColumns;
            const storedHiddenColumns = getLocalStorageItem(HIDDEN_COLUMNS_KEY);
            return storedHiddenColumns
                ? JSON.parse(storedHiddenColumns)
                : [ID_KEY];
        }, [persistedFilterState]);

        // Initialize the state and dispatch for the ticket table context
        const [state, dispatch] = useReducer<
            React.Reducer<TicketTableContextValue, { type: TableContextActions; payload: any }>
        >(ticketTableContextReducer, {
            ...INITIAL_CONTEXT_STATE,
            hiddenColumns: initialHiddenColumns,
            filters: persistedFilterState?.filters ?? INITIAL_CONTEXT_STATE.filters,
            perPage: persistedFilterState?.perPage ?? INITIAL_CONTEXT_STATE.perPage,
            showClosed: false,
        });

    // Initialize the data fetching and mutation hooks for the ticket table context
    const { loading, getTickets } = useGetTickets();
    const { loading: deleteLoading, deleteTicket: deleteItem } = useDeleteTicket();
    const { updateTicket, loading: updateTicketLoading } = useUpdateTicket();
    const { setError } = useScreenAlert();
    const { checkUserAuthentication } = useUserContext();

    //Begin Dispatch Action Declaration for Ticket Table Context

    // Get all common table actions from the shared hook
    const commonActions = useCommonTableActions<TicketTableData, TicketTableFilter>(
        dispatch,
        getTickets,
        deleteItem,
        updateTicket,
        state,
    );

    // Destructure common actions for easy access
    const {
        setItems,
        refreshItems,
        deleteItems,
        editItems,
        addFilter,
        removeFilter,
        removeAllFilters,
        setSearchValue,
        clearSearchValue,
        selectItem,
        setPage,
        setPerPage,
        addHiddenColumn,
        removeHiddenColumn,
    } = commonActions;

    // Context-specific actions below
    //CUSTOM ACTION FOR TICKET CONTEXT TO TOGGLE SHOW CLOSED TICKETS
    const toggleShowClosedTickets = useCallback(() => {
        dispatch({
            type: "TOGGLE_SHOW_CLOSED_TICKETS" as TableContextActions,
            payload: undefined
        });
    }, []);
    // End Action Declaration for Ticket Table Context

    //Begin Data Load
    useTableDataFetcher({
        state,
        loading,
        otherLoadingStates: [deleteLoading, updateTicketLoading],
        fetchData: getTickets,
        getFetchParams: buildFetchParams,
        setItems,
        checkUserAuthentication,
        setError,
    });
    //End Data Load

    // Persist filters + hidden columns + page size (per user/subsite) whenever
    // any of them change — survives refresh and login.
    useEffect(() => {
        const hiddenColumnsUpdate = state.hiddenColumns.includes(ID_KEY)
            ? state.hiddenColumns
            : [...state.hiddenColumns, ID_KEY];
        savePersistedTableFilterState<TicketTableFilter>(
            TICKET_FILTER_TABLE_KEY,
            siteId,
            TICKET_FILTER_SCHEMA_VERSION,
            {
                filters: state.filters,
                hiddenColumns: hiddenColumnsUpdate,
                perPage: state.perPage,
            },
            instanceId,
        );
    }, [state.filters, state.hiddenColumns, state.perPage, siteId, instanceId]);

    // Memoize the combined loading state to prevent unnecessary re-renders
    const isLoading = useMemo(
        () => state.loading || loading || updateTicketLoading || deleteLoading,
        [state.loading, loading, updateTicketLoading, deleteLoading]
    );

    // These are the values that are loaded into the TicketTableDispatchContext
    const dispatchValue = useMemo(
        () => ({
            // Dispatch actions for the ticket table context
            setItems,
            addFilter,
            removeFilter,
            removeAllFilters,
            setSearchValue,
            clearSearchValue,
            editItems,
            selectItem,
            refreshItems,
            deleteItems,
            setPage,
            setPerPage,
            addHiddenColumn,
            removeHiddenColumn,
            toggleShowClosedTickets,
        }),
        [
            setItems,
            addFilter,
            removeFilter,
            removeAllFilters,
            setSearchValue,
            clearSearchValue,
            editItems,
            selectItem,
            refreshItems,
            deleteItems,
            setPage,
            setPerPage,
            addHiddenColumn,
            removeHiddenColumn,
            toggleShowClosedTickets,
        ]
    );

    return (
        <TicketTableDispatchContext.Provider value={dispatchValue}>
            <TicketTableContext.Provider value={{
                ...state,
                loading: isLoading
            }}>
                {children}
            </TicketTableContext.Provider>
        </TicketTableDispatchContext.Provider>
    );

};

export const useTicketTableContext = () => {
  const context = useContext(TicketTableContext);
  if (!context) {
    throw new Error(
      "useTicketTableContext must be used within an TicketTableContextProvider"
    );
  }
  return context;
};

export const useTicketTableDispatch = () => {
  const dispatchContext = useContext(TicketTableDispatchContext);
  if (!dispatchContext) {
    throw new Error(
      "useTicketTableDispatch must be used within an TicketTableContextProvider"
    );
  }
  return dispatchContext;
};
